import { isNumber, isUndefined } from 'lodash';
import { v4 as uuid } from 'uuid';
import { getFramework, ResourceThumbnail, type IFramework } from '@/framework';
import { getLocaleStore } from '@/i18n/index';
import { base64Kit } from '@/utils/base64-kit';
import { loadImageAsync } from '@/utils/file-kit';
import { convertWebpToImage } from '@/utils/image-resource-kit';
import { deleteExtraInfo, isEmptyEffect } from '@/utils/layer-kit';
import { getLayerOutputDraft, markLayerOutputDraft } from '@/utils/layer-output-draft-kit';
import {
    GroupRoleType,
    LayerType,
    MaterialIdsType,
    MIN_IMG_SIZE,
    StretchOptionType,
    type IActiveBaseInfo,
    type IExportBehavior,
    type IGroupLayerProps,
    type IImageLayerProps,
    type IImageProps,
    type ILayerBehavior,
    type ILayerOriginInfo,
    type ILayerProps,
    type ISize,
} from '@/version-types';
import { EdgelessImageLayer } from './edgeless-layer';
import ImageOperate from './operate/image-operate';
import { getImageEffectResultImgSize } from './plugins/image-effect/runtime/constants';
import {
    getImageLayerEffectArtifact,
    getImageLayerEffectPosition,
    rebuildImageLayerEffect,
} from './render/render-to-canvas';
import { renderLayerToPPT } from './render/render-to-ppt';
import { getImageLayerCanvas, renderLayerToStage } from './render/render-to-stage';

function getBehavior(identity: string) {
    function fixEffectData(data: IImageLayerProps) {
        // 修复特效丢失
        if (data.effect && (data.effect.glow || data.effect.stroke || data.effect.shadow)) {
            if (!isNumber(data.effect.effectAlpha)) {
                data.effect.effectAlpha = 1;
            }
            if (!data.effect.glow) {
                data.effect.glow = [];
            }
            if (!data.effect.stroke) {
                data.effect.stroke = [];
            }
            if (!data.effect.shadow) {
                data.effect.shadow = [];
            }
        }

        // 如果图层没有特效，则设置特效的截图url为空
        if (isEmptyEffect(data) && data.effect) {
            data.effect.screenshotUrl = '';
        }
    }

    const checkIsPuzzle = (layer: ILayerProps, framework: IFramework) => {
        const parentId = layer.parentId;
        if (!parentId) return false;
        const parentLayer = framework.service.flatLayer.getLayer(parentId);

        if (!parentLayer) return false;
        return (
            parentLayer.layerType === LayerType.GROUP_LAYER &&
            (parentLayer.data as IGroupLayerProps).extension?.role === GroupRoleType.PUZZLE
        );
    };

    /**
     * 修复图层宽高为 0 的图层
     * @param layer - 图层
     */
    async function fixLayerSize(layer: ILayerProps) {
        if (layer.width <= 0 || layer.height <= 0) {
            const data = layer.data as IImageLayerProps;
            if (data.url) {
                const img = await loadImageAsync(data.url);
                if (img.width && img.height) {
                    layer.width = img.width;
                    layer.height = img.height;
                    data.naturalWidth = img.width;
                    data.naturalHeight = img.height;
                }
            }
        }
    }

    const layerBehavior: ILayerBehavior = {
        getEdgelessObject(layer: ILayerProps) {
            return new EdgelessImageLayer(layer.id, getFramework(identity));
        },

        async savePPTLayerAsync(slide, layer, stageWidth, stageHeight) {
            return renderLayerToPPT(slide, layer, stageWidth, stageHeight, identity);
        },

        viewNeedBasicOperation() {
            return true;
        },

        onBeforeAddOrUpdate(layer: ILayerProps) {
            const data = layer.data as IImageLayerProps;
            // console.log('onBeforeAddOrUpdate 1=>', data.url);

            data.url = base64Kit.getCode(data.url);

            if (isUndefined(layer.__renderAiWatermark)) {
                layer.__renderAiWatermark = !!layer.extraInfo?.watermarkURL;
            }

            fixEffectData(data);
        },

        async onBeforeOutputAsync(layer: ILayerProps, context) {
            const data = layer.data as IImageLayerProps;
            const persistedEffect = data.effect && {
                screenshotUrl: data.effect.screenshotUrl,
                effectPosition: data.effect.effectPosition,
            };
            const registration = getFramework(identity).service.registration;
            registration
                .getInstance<{ projectOutputDraft: (outputLayer: ILayerProps) => boolean }>('ImageFilterPlugin')
                ?.projectOutputDraft(layer);
            registration
                .getInstance<{ projectOutputDraft: (outputLayer: ILayerProps) => boolean }>('ImageEffectPlugin')
                ?.projectOutputDraft(layer);
            const object = getFramework(identity).service.edgelessCanvas?.getByLayerId(layer.id) as
                | EdgelessImageLayer
                | undefined;
            await object?.projectOutputEffect?.(layer);
            // 未匹配到已成功显示的结果时，才使用旧配方兜底。
            if (context.purpose === 'recipe' || context.usePersistedImageEffect) {
                if (
                    data.effect &&
                    !data.effect.screenshotUrl &&
                    !getLayerOutputDraft(layer)?.effectCanvas &&
                    persistedEffect?.screenshotUrl
                ) {
                    data.effect.screenshotUrl = persistedEffect.screenshotUrl;
                    data.effect.effectPosition = persistedEffect.effectPosition;
                }
            }
            if (context.usePersistedImageEffect) {
                markLayerOutputDraft(layer).usePersistedImageEffect = true;
            }
        },

        async onBeforeSaveAsync(layer: ILayerProps, _isAutoSave?: boolean) {
            const data = layer.data as IImageLayerProps;
            const outputDraft = getLayerOutputDraft(layer);
            if (outputDraft?.sourceCanvas) {
                data.url = base64Kit.getCode(outputDraft.sourceCanvas.toDataURL('image/png'), uuid());
            }
            deleteExtraInfo(layer);
            data.url = convertWebpToImage(data.url);
            data.maskURL = convertWebpToImage(data.maskURL);
            data.cutoutMaskURL = convertWebpToImage(data.cutoutMaskURL);
            fixEffectData(data);
            // 在 URL 持久化改变输入指纹前读取已生成的结果，保存过程不调用 WASM 重算。
            if (!isEmptyEffect(data) && data.effect) {
                if (outputDraft?.effectCanvas) {
                    data.effect.screenshotUrl = base64Kit.getCode(outputDraft.effectCanvas.toDataURL('image/png'));
                    data.effect.effectPosition = outputDraft.effectPosition;
                } else {
                    const artifact = getImageLayerEffectArtifact(layer);
                    if (artifact?.screenshotUrl) {
                        data.effect.screenshotUrl = artifact.screenshotUrl;
                        data.effect.effectPosition = artifact.effectPosition;
                    }
                }
            } else if (data.effect) {
                data.effect.screenshotUrl = '';
                delete data.effect.effectPosition;
            }
            await layerBehavior.convertCodeToHttpUrl?.(layer);
            await fixLayerSize(layer);
        },
        async getResultCanvasAsync(
            layer: ILayerProps,
            resultSize: ISize,
            includeEffect = true,
        ): Promise<HTMLCanvasElement | undefined> {
            return renderLayerToStage(layer, resultSize, identity, includeEffect);
        },

        async getLayerEffectImageAsync(layer: ILayerProps, setOpacity = false) {
            const { data, width, height } = layer;
            const { effect } = data as IImageLayerProps;
            if (!effect || isEmptyEffect(data as IImageLayerProps)) {
                return null;
            }

            const imgEffectResultImgSize = getImageEffectResultImgSize(data as IImageLayerProps);
            const cachedArtifact = getImageLayerEffectArtifact(layer);
            let screenshotUrl = cachedArtifact?.screenshotUrl || effect.screenshotUrl;
            let effectPosition = getImageLayerEffectPosition(layer);
            if (!screenshotUrl) {
                const artifact = await rebuildImageLayerEffect(layer);
                screenshotUrl = artifact.screenshotUrl;
                effectPosition = artifact.effectPosition;
            }
            const canvas = document.createElement('canvas');
            if (effectPosition) {
                canvas.width = effectPosition.right - effectPosition.left;
                canvas.height = effectPosition.bottom - effectPosition.top;
            } else {
                canvas.width = width * imgEffectResultImgSize;
                canvas.height = height * imgEffectResultImgSize;
            }
            const ctx = canvas.getContext('2d');
            if (ctx && setOpacity) {
                ctx.globalAlpha = layer.opacity;
            }
            const url = await base64Kit.getRemoteUrlAsync(screenshotUrl);
            const img = await loadImageAsync(url);

            ctx?.drawImage(img, 0, 0, img.width, img.height, 0, 0, canvas.width, canvas.height);
            return canvas;
        },

        // needScaleCanvas 当原图超过一定大小的时候是否压缩
        async getLayerImageAsync(
            layer: ILayerProps,
            globalAlpha = true,
            needScaleCanvas = true,
            noNeedBorderRadius = false,
            useLayerOpacity = true,
        ) {
            void needScaleCanvas;

            return getImageLayerCanvas(layer, identity, {
                noEffect: true,
                globalAlpha,
                noNeedBorderRadius,
                useLayerOpacity,
            });
        },

        getActiveBaseInfo(layer: ILayerProps): IActiveBaseInfo {
            const data = layer.data as IImageLayerProps;
            const { naturalHeight, naturalWidth } = data;
            return {
                cutWidth: Math.min(naturalWidth, MIN_IMG_SIZE),
                cutHeight: Math.min(naturalHeight, MIN_IMG_SIZE),
            };
        },
        getScaleMinRatio(originInfo: ILayerOriginInfo, layer: ILayerProps): number {
            const { width, cutWidth } = originInfo;
            const offWidth = width - cutWidth;
            return (width - offWidth) / width;
        },
        getScaleIsNeedLimitSize(): boolean {
            return true;
        },

        whenGroupCanResize(layer: ILayerProps) {
            const { effect, borderRadius } = layer.data as IImageLayerProps;
            let canResize = !effect && !borderRadius;

            if (checkIsPuzzle(layer, getFramework(identity))) {
                canResize = true;
            }

            return {
                horizontal: canResize,
                vertical: canResize,
            };
        },

        onBeforeResizeUpdate(layer: ILayerProps) {
            const { imageTransform, naturalWidth, naturalHeight, stretchOption } = layer.data as IImageLayerProps;

            if (stretchOption === StretchOptionType.cover) {
                // 智能模式 每次用原始图片和当前图层大小算出比例
                const newWidthRatio = naturalWidth / layer.width;
                const newHeightRatio = naturalHeight / layer.height;

                const ratio = newWidthRatio < newHeightRatio ? newWidthRatio : newHeightRatio;

                imageTransform.widthRatio = naturalWidth / ratio / layer.width;
                imageTransform.heightRatio = naturalHeight / ratio / layer.height;
            }
        },

        checkIsPhoto(layer: ILayerProps) {
            return !!layer?.materialIds?.[MaterialIdsType.PHOTO_MATERIAL]?.length;
        },

        checkIsAiText(layer: ILayerProps) {
            return !!(layer?.data as IImageLayerProps)?.aiTextExtension;
        },

        async convertCodeToHttpUrl(layer: ILayerProps) {
            const data = layer.data as IImageLayerProps;
            if (data.url) {
                data.url = await base64Kit.getRemoteUrlAsync(data.url);
            }
            if (data.maskURL) {
                data.maskURL = await base64Kit.getRemoteUrlAsync(data.maskURL);
            }
            if (data.cutoutMaskURL) {
                data.cutoutMaskURL = await base64Kit.getRemoteUrlAsync(data.cutoutMaskURL);
            }
            if (data.effect?.screenshotUrl) {
                data.effect.screenshotUrl = await base64Kit.getRemoteUrlAsync(data.effect.screenshotUrl);
            }
        },

        getMenuOverlapName(layer: ILayerProps) {
            const { $t } = getLocaleStore();
            return $t((v) => v.layers.image);
        },

        getMenuOverlapIcon(layer: ILayerProps) {
            const resource = getFramework(identity).service.edgelessCanvas.getByLayerId(layer.id)?.resource;
            const { url } = layer.data as IImageLayerProps;
            return <ResourceThumbnail resource={resource} fallbackUrl={base64Kit.getLocalUrl(url)} />;
        },

        onBeforeScaleUpdate(layer, { originInfo }) {
            const { effect } = layer.data as IImageLayerProps;
            if (!originInfo || !effect?.effectPosition) return;
            const { width: oldWidth, height: oldHeight } = originInfo;
            const { width, height } = layer;
            const scaleX = width / oldWidth;
            const scaleY = height / oldHeight;
            const { left, top, right, bottom } = effect.effectPosition;
            effect.effectPosition.left = left * scaleX;
            effect.effectPosition.top = top * scaleY;
            effect.effectPosition.right = right * scaleX;
            effect.effectPosition.bottom = bottom * scaleY;
        },

        checkDisableStretch(layer: ILayerProps) {
            return (layer.data as IImageProps)?.stretchOption === StretchOptionType.none;
        },

        customActiveOperate(layer) {
            if (getFramework(identity).service.edgelessCanvas.isEdgelessMode) return null;
            return <ImageOperate activeLayer={layer} />;
        },

        resizeOnlyUpdateBaseInfo(layer: ILayerProps) {
            const { stretchOption, maskURL } = layer.data as IImageLayerProps;
            // 带蒙版的智能拉伸需要在拖动中按最新裁切比例重绘，避免拉伸旧合成图。
            return !(stretchOption === StretchOptionType.cover && maskURL);
        },

        scaleOnlyUpdateBaseInfo(layer: ILayerProps) {
            return true;
        },
    };
    return layerBehavior;
}

const exportBehavior: IExportBehavior = {
    layerType: LayerType.IMAGE_LAYER,
    getBehavior,
};

export default exportBehavior;
